import { useEffect, useState } from 'react';
import {
  Controller,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';

import type {
  CreateTravelerDto,
  TravelerResponseDto,
  UpdateTravelerDto,
} from '@bandido/shared/dtos';

import { useGetAgencyById } from '@frontend/features/agencies/queries';
import { agencyCountryCode } from '@frontend/features/travel-services/agencyCountry';
import { CountrySelect as TravelerCountrySelect } from '@frontend/features/travel-services/components/CountrySelect';
import { PhoneField } from '@frontend/features/travel-services/components/PhoneField';

import {
  hasTravelerFormChanges,
  travelerFormPayload,
  travelerToFormValues,
} from '../travelerForm';
import type { TravelerFormValues } from '../types';

interface Props {
  open: boolean;
  agencyId: number;
  traveler?: TravelerResponseDto;
  isSaving: boolean;
  error?: string;
  onClose: () => void;
  onSave: (
    input: CreateTravelerDto | UpdateTravelerDto,
  ) => Promise<void>;
}

export const TravelerFormDialog = ({
  open,
  agencyId,
  traveler,
  isSaving,
  error,
  onClose,
  onSave,
}: Props) => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    register,
    reset,
    getFieldState,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<TravelerFormValues>({
    defaultValues: travelerToFormValues(traveler),
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'documents',
  });
  const { data: agency } = useGetAgencyById({ id: agencyId });
  const defaultCountry = agencyCountryCode(agency?.country);
  const [phoneInputInvalid, setPhoneInputInvalid] = useState(false);

  useEffect(() => {
    if (open) {
      setPhoneInputInvalid(false);
      reset(travelerToFormValues(traveler));
    }
  }, [open, reset, traveler]);

  // La agencia suele resolver después de abrir el modal. Se completan sólo
  // campos todavía intactos para no pisar una elección del asesor.
  useEffect(() => {
    if (!open || traveler || !defaultCountry) return;
    if (!getFieldState('nationality').isDirty) {
      setValue('nationality', defaultCountry);
    }
    if (!getFieldState('countryOfResidence').isDirty) {
      setValue('countryOfResidence', defaultCountry);
    }
  }, [defaultCountry, getFieldState, open, setValue, traveler]);

  const submit = handleSubmit(async (values) => {
    if (
      phoneInputInvalid &&
      (!traveler || getFieldState('phone').isDirty)
    ) {
      setError('phone', {
        type: 'validate',
        message: t('travelers.validation.phone'),
      });
      return;
    }
    const initialValues = traveler
      ? travelerToFormValues(traveler)
      : undefined;
    if (
      traveler &&
      initialValues &&
      !hasTravelerFormChanges(values, initialValues)
    ) {
      onClose();
      return;
    }
    await onSave(
      travelerFormPayload(
        values,
        Boolean(traveler),
        initialValues,
      ),
    );
  });

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} fullWidth maxWidth="md">
      <Box component="form" onSubmit={submit} noValidate>
        <DialogTitle>
          {traveler
            ? t('travelers.form.editTitle')
            : t('travelers.form.createTitle')}
        </DialogTitle>
        <DialogContent dividers>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }}
            gap={2}
          >
            <TextField
              label={t('travelers.fields.firstName')}
              size="small"
              required
              error={Boolean(errors.firstName)}
              helperText={errors.firstName?.message}
              inputProps={{ maxLength: 80 }}
              {...register('firstName', {
                required: t('travelers.validation.required'),
              })}
            />
            <TextField
              label={t('travelers.fields.lastName')}
              size="small"
              required
              error={Boolean(errors.lastName)}
              helperText={errors.lastName?.message}
              inputProps={{ maxLength: 80 }}
              {...register('lastName', {
                required: t('travelers.validation.required'),
              })}
            />
            <TextField
              label={t('travelers.fields.title')}
              size="small"
              inputProps={{ maxLength: 20 }}
              {...register('title')}
            />
            <TextField
              label={t('travelers.fields.birthDate')}
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              {...register('birthDate')}
            />
            <TextField
              label={t('travelers.fields.email')}
              type="email"
              size="small"
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
              inputProps={{ maxLength: 120 }}
              {...register('email', {
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: t('travelers.validation.email'),
                },
              })}
            />
            <Controller
              control={control}
              name="phone"
              render={({ field }) => (
                <PhoneField
                  required={false}
                  error={Boolean(errors.phone)}
                  label={t('travelers.fields.phone')}
                  value={field.value}
                  onChange={(value) => {
                    clearErrors('phone');
                    field.onChange(value);
                  }}
                  defaultCountry={defaultCountry}
                  onInvalidChange={setPhoneInputInvalid}
                />
              )}
            />
            <Controller
              control={control}
              name="nationality"
              render={({ field }) => (
                <TravelerCountrySelect
                  fullWidth
                  label={t('travelers.fields.nationality')}
                  value={field.value}
                  onChange={field.onChange}
                  error={Boolean(errors.nationality)}
                  helperText={errors.nationality?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="countryOfResidence"
              render={({ field }) => (
                <TravelerCountrySelect
                  fullWidth
                  label={t('travelers.fields.countryOfResidence')}
                  value={field.value}
                  onChange={field.onChange}
                  error={Boolean(errors.countryOfResidence)}
                  helperText={errors.countryOfResidence?.message}
                />
              )}
            />
          </Box>

          <Box mt={3} display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="subtitle2">
                {t('travelers.documents.title')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('travelers.documents.privateHint')}
              </Typography>
            </Box>
            <Button
              startIcon={<Add />}
              onClick={() =>
                append({
                  type: 'passport',
                  issuingCountry: defaultCountry ?? '',
                  number: '',
                  expiresOn: '',
                })
              }
              size="small"
            >
              {t('travelers.documents.add')}
            </Button>
          </Box>

          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            {fields.map((field, index) => (
              <Box
                key={field.id}
                display="grid"
                gridTemplateColumns={{
                  xs: '1fr',
                  md: '1.1fr 1.4fr 1.2fr 1fr auto',
                }}
                gap={1}
                alignItems="start"
                p={2}
                border={1}
                borderColor="divider"
                borderRadius={1}
              >
                <Controller
                  control={control}
                  name={`documents.${index}.type`}
                  rules={{ required: t('travelers.validation.required') }}
                  render={({ field: typeField, fieldState }) => (
                    <TextField
                      {...typeField}
                      select
                      label={t('travelers.documents.type')}
                      size="small"
                      required
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    >
                      <MenuItem value="passport">
                        {t('travelers.documents.types.passport')}
                      </MenuItem>
                      <MenuItem value="ci">
                        {t('travelers.documents.types.ci')}
                      </MenuItem>
                      <MenuItem value="dni">
                        {t('travelers.documents.types.dni')}
                      </MenuItem>
                      {typeField.value &&
                        !['passport', 'ci', 'dni'].includes(typeField.value) ? (
                          <MenuItem value={typeField.value}>
                            {typeField.value}
                          </MenuItem>
                        ) : null}
                    </TextField>
                  )}
                />
                <Controller
                  control={control}
                  name={`documents.${index}.issuingCountry`}
                  render={({ field: countryField }) => (
                    <TravelerCountrySelect
                      fullWidth
                      label={t('travelers.documents.country')}
                      value={countryField.value}
                      onChange={countryField.onChange}
                      error={Boolean(errors.documents?.[index]?.issuingCountry)}
                      helperText={
                        errors.documents?.[index]?.issuingCountry?.message
                      }
                    />
                  )}
                />
                <TextField
                  label={t('travelers.documents.number')}
                  size="small"
                  required
                  error={Boolean(errors.documents?.[index]?.number)}
                  helperText={errors.documents?.[index]?.number?.message}
                  autoComplete="off"
                  {...register(`documents.${index}.number`, {
                    required: t('travelers.validation.required'),
                  })}
                />
                <Controller
                  control={control}
                  name={`documents.${index}.expiresOn`}
                  render={({ field: dateField }) => (
                    <TextField
                      {...dateField}
                      label={t('travelers.documents.expiresOn')}
                      type="date"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
                <IconButton
                  aria-label={t('travelers.documents.remove')}
                  color="error"
                  onClick={() => remove(index)}
                  sx={{ justifySelf: { xs: 'end', md: 'auto' } }}
                >
                  <Delete />
                </IconButton>
              </Box>
            ))}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSaving}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={isSaving}>
            {isSaving ? t('common.loading') : t('common.save')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
